import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Container,
    Card,
    Badge,
    Button,
    Tab,
    Table,
    Form,
    Modal,
    Alert,
    Row,
    Col,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import DashboardNav, {
    generateChildId,
} from '@/components/common/DashboardNav';
import ProgramDeadlinesSection from '@/components/common/ProgramDeadlinesSection';
import BudgetStatus, { budgetOrNone } from '@/features/programs/BudgetStatus';
import CoordinatorSettings from '@/features/programs/CoordinatorSettings';
import ProgramTermHeader, {
    MissingDatesAlert,
} from '@/components/common/ProgramTermHeader';
import ProgramPageLinkCard from '@/features/programs/ProgramPageLinkCard';
import ProgramCoursePrefixes from '@/components/common/ProgramCoursePrefixes';
import DecisionEmailTemplates from '@/features/programs/DecisionEmailTemplates';
import programService from '@/shared/services/programService';
import TrainingDatesCard from '@/features/programs/TrainingDatesCard';
import ProgramNotificationSettings from '@/features/programs/ProgramNotificationSettings';
import ProgramDecisionsList from '@/features/programs/ProgramDecisionsList';
import CoordinatorOverview from '@/features/programs/CoordinatorOverview';
import ListFilterNote from '@/features/programs/ListFilterNote';
import UnsubmittedDrafts from '@/features/programs/UnsubmittedDrafts';
import {
    APPLICATION_LISTS,
    DRAFTS_SHOW,
    listFor,
} from '@/features/programs/overviewLists';
import { useDashboardTab } from '@/shared/hooks/useDashboardTab';
import { useListLinkReset } from '@/shared/hooks/useListLinkReset';
import applicationService from '@/shared/services/applicationService';
import assignmentService from '@/shared/services/assignmentService';
import courseService from '@/shared/services/courseService';
import termService from '@/shared/services/termService';
import decisionService from '@/shared/services/decisionService';
import budgetService from '@/shared/services/budgetService';
import studentApplicationService from '@/shared/services/studentApplicationService';
import { useFeature } from '@/shared/contexts/FeatureFlagContext';
import '@/features/programs/CoordinatorDashboard.css';
import FacultyCell from '@/features/programs/FacultyCell';
import RequestCardList from '@/features/programs/RequestCardList';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { badgeTextFor } from '@/shared/utils/badgeText';
import { positionChoiceSummary } from '@/shared/utils/positionChoiceSummary';
import SortableTh from '@/components/common/SortableTh';
import TableControlsBar from '@/components/common/TableControlsBar';
import TableEmpty from '@/components/common/TableEmpty';
import {
    PHONE_QUERY,
    SECONDARY_COLUMN,
    SECONDARY_COLUMN_LG,
} from '@/shared/utils/tableControls';
import { useTableControls } from '@/shared/hooks/useTableControls';
import { useTableParams } from '@/shared/hooks/useTableParams';
import {
    ASSIGNMENT_STATUS_ORDER,
    STUDENT_APPLICATION_STATUS_OPTIONS,
    assignmentStatusLabel,
    courseCode,
    courseColumn,
    courseOptionsFor,
    dateColumn,
    departmentOf,
    departmentOptionsFor,
    facultyColumn,
    modalityColumn,
    modalityText,
    personColumn,
    sessionsText,
    statusColumn,
    studentApplicationColumns,
} from '@/features/programs/coordinatorColumns';
import { requestInstructorName } from '@/shared/utils/requestInstructor';
import { isBeingRevisedByFaculty } from '@/shared/utils/applicationStatus';
import StatusPill from '@/components/common/StatusPill';
import LoadingState from '@/components/common/LoadingState';
import LoadError from '@/components/common/LoadError';
import settledRead from '@/shared/utils/settledRead';
import { STUDENT_APPLICATION_PILL, pillFor } from '@/shared/utils/statusPills';
import {
    PROGRAM_OUTCOME_OPTIONS,
    PROGRAM_OUTCOME_ORDER,
} from '@/features/programs/programOutcomeStatus';
import { formatAnswerDay } from '@/shared/utils/deadline';
import { termDisplayName } from '@/shared/utils/termLabel';

// SI's own decision on the request, not the request's shared status, which on
// a request two programs hold is the other program's award as readily as SI's.
const appStatusPill = (app) => (
    <StatusPill
        family="programOutcome"
        value={app.programOutcome}
        application={app}
    />
);

// Shown in the list but not open for a decision: withdrawn is final, and a
// returned application is back with the faculty member until they resubmit.
const VIEW_ONLY_STATUSES = ['withdrawn', 'revision_requested'];

// "BIO 1080, section 001", the way a card names the request
const requestHeading = (app) => {
    const section =
        app.courseConfirmation?.sectionNumber || app.course?.section;
    return [courseCode(app.course) || 'Course', section && `section ${section}`]
        .filter(Boolean)
        .join(', ');
};

const getSIRank = (app) => {
    const siPref = app.programPreferences?.find((p) => p.programType === 'si');
    return siPref?.rank || '-';
};

const applicationDepartment = (app) => departmentOf(app.course);

const APPLICATION_COLUMNS = [
    facultyColumn(),
    courseColumn((app) => app.course),
    modalityColumn(),
    {
        key: 'rank',
        label: 'SI Rank',
        type: 'number',
        secondary: true,
        value: (app) => {
            const rank = getSIRank(app);
            return rank === '-' ? null : rank;
        },
        csv: (app) => `#${getSIRank(app)}`,
    },
    {
        ...dateColumn('submitted', 'Submitted', (app) => app.submittedAt),
        secondary: true,
    },
    statusColumn(
        PROGRAM_OUTCOME_ORDER,
        (app) => app.programOutcome,
        (app) =>
            pillFor('programOutcome', app.programOutcome, { application: app })
                .label
    ),
];

const ASSIGNMENT_COLUMNS = [
    personColumn('leader', 'SI Leader', (asg) => asg.peerSupporter),
    courseColumn((asg) => asg.course),
    {
        key: 'hours',
        label: 'Hours/Week',
        type: 'number',
        secondary: true,
        searchable: false,
        value: (asg) => asg.hoursPerWeek || null,
    },
    {
        key: 'schedule',
        label: 'Schedule',
        type: 'text',
        secondary: true,
        value: (asg) => sessionsText(asg.schedule?.sessions),
    },
    statusColumn(
        ASSIGNMENT_STATUS_ORDER,
        (asg) => asg.status,
        (asg) => assignmentStatusLabel(asg.status)
    ),
];

const COURSE_SI_STATUS = {
    needs: 'Needs SI',
    assigned: 'SI Assigned',
};

const COURSE_STATUS_OPTIONS = [
    { value: 'needs', label: 'Needs an SI leader' },
    { value: 'assigned', label: 'Has an SI leader' },
];

const STUDENT_APPLICATION_COLUMNS = studentApplicationColumns(
    STUDENT_APPLICATION_PILL,
    positionChoiceSummary
);

const assignmentCourseOf = (asg) => courseCode(asg.course);
const applicationStatusValue = (app) => app.programOutcome;
const studentApplicationStatusOf = (app) => app.status;

// Tab keys that moved into the Settings tab, with the section each landed in
const SETTINGS_ALIASES = {
    notifications: { tab: 'settings', show: 'notifications' },
    'decision-emails': { tab: 'settings', show: 'decision-emails' },
};

function SICoordinatorDashboard() {
    const navigate = useNavigate();
    const location = useLocation();
    const studentApplicationEnabled = useFeature('studentApplication');
    const [successMessage, setSuccessMessage] = useState(
        location.state?.success || null
    );
    const [terms, setTerms] = useState([]);
    const [selectedTerm, setSelectedTerm] = useState(null);
    const [stats, setStats] = useState(null);
    const [applications, setApplications] = useState([]);
    // The server's real total when the whole-term list hit its backstop
    const [cutOffTotal, setCutOffTotal] = useState(null);
    const [assignments, setAssignments] = useState([]);
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [budgetFailed, setBudgetFailed] = useState(false);
    const [studentAppsFailed, setStudentAppsFailed] = useState(false);
    const { activeTab, show, selectTab, clearShow } = useDashboardTab(
        [
            'overview',
            'applications',
            'decisions',
            studentApplicationEnabled && 'student-applications',
            'assignments',
            'courses',
            'settings',
        ].filter(Boolean),
        'overview',
        SETTINGS_ALIASES
    );
    const [deadlinesOpen, setDeadlinesOpen] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [showDecisionModal, setShowDecisionModal] = useState(false);
    const [decisionNotes, setDecisionNotes] = useState('');
    const [submittingDecision, setSubmittingDecision] = useState(false);
    const [budget, setBudget] = useState(null);
    const [studentApplications, setStudentApplications] = useState([]);

    // Load terms on mount
    useEffect(() => {
        const loadTerms = async () => {
            try {
                const data = await termService.getAllTerms();
                setTerms(data);
                const active = data.find((t) => t.isActive) || data[0];
                if (active) {
                    setSelectedTerm(active);
                } else {
                    setLoading(false);
                }
            } catch (_err) {
                setError('Failed to load terms.');
                setLoading(false);
            }
        };
        loadTerms();
    }, []);

    const loadTermData = useCallback(
        async (termCode) => {
            try {
                setLoading(true);
                setError(null);
                setLoadFailed(false);

                const [
                    statsData,
                    appsData,
                    assignmentsData,
                    coursesData,
                    budgetData,
                    studentAppsData,
                ] = await Promise.all([
                    programService.getSIStats(termCode),
                    applicationService.getApplicationsByProgram('si', {
                        term: termCode,
                    }),
                    // The tab and the header count the same people: the
                    // program stats count active assignments
                    assignmentService.getAssignmentsByProgram('si', {
                        term: termCode,
                        status: 'active',
                    }),
                    courseService.getCoursesEligibleForProgram(termCode, 'si'),
                    settledRead(
                        budgetOrNone(budgetService.getBudget('si', termCode))
                    ),
                    // Not asked for at all while the feature is off: the server
                    // gates this endpoint with the same flag, so the request
                    // can only 404, and the tab that would show the result is
                    // not rendered either. The .catch here is for a request
                    // that was worth making and failed, not for one we already
                    // know the answer to.
                    studentApplicationEnabled
                        ? settledRead(
                              studentApplicationService.getApplicationsByProgram(
                                  'si',
                                  { term: termCode }
                              )
                          )
                        : { value: [], failed: false },
                ]);

                setStats(statsData);
                setApplications(appsData);
                setCutOffTotal(appsData.truncated ? appsData.total : null);
                setAssignments(assignmentsData);
                // Already narrowed to this program, and to the whole term
                // rather than one page of it, by the service call above.
                setCourses(coursesData);
                setBudget(budgetData.value);
                setBudgetFailed(budgetData.failed);
                setStudentApplications(studentAppsData.value || []);
                setStudentAppsFailed(studentAppsData.failed);
            } catch (_err) {
                setLoadFailed(true);
            } finally {
                setLoading(false);
            }
        },
        [studentApplicationEnabled]
    );

    // Load data when selected term changes
    useEffect(() => {
        if (selectedTerm?.code) {
            loadTermData(selectedTerm.code);
        }
    }, [selectedTerm?.code, loadTermData]);

    const handleTermChange = (code) => {
        const term = terms.find((t) => t.code === code);
        if (term) setSelectedTerm(term);
    };

    const handleTermUpdated = (updatedTerm) => {
        setSelectedTerm(updatedTerm);
        setTerms((prev) =>
            prev.map((t) => (t.code === updatedTerm.code ? updatedTerm : t))
        );
    };

    const applicationList =
        activeTab === 'applications'
            ? listFor(APPLICATION_LISTS.si, show)
            : null;
    const listedApplications = applicationList
        ? applications.filter(applicationList.matches)
        : applications;
    const applicationDepartmentOptions = departmentOptionsFor(
        applications,
        (app) => app.course
    );
    const applicationControls = useTableControls({
        rows: listedApplications,
        columns: APPLICATION_COLUMNS,
        statusOf: applicationStatusValue,
        departmentOf: applicationDepartment,
        departmentOptions: applicationDepartmentOptions,
        persist: useTableParams('apps'),
    });
    const asCards = useMediaQuery(PHONE_QUERY);
    const requestCardOf = (app) => {
        const rank = getSIRank(app);
        return {
            key: app._id,
            heading: requestHeading(app),
            title: app.course?.title,
            faculty: <FacultyCell app={app} />,
            pill: appStatusPill(app),
            facts: [
                modalityText(app),
                rank !== '-' && `SI choice ${rank}`,
                app.submittedAt &&
                    `Submitted ${formatAnswerDay(app.submittedAt)}`,
            ].filter(Boolean),
            actionLabel: 'Review request',
            onAction: () => handleOpenDecision(app),
        };
    };
    const {
        setSearch: setApplicationSearch,
        setStatus: setApplicationStatus,
        setDepartment: setApplicationDepartment,
    } = applicationControls;

    // A list an Overview link opened starts from exactly the rows it counted
    useListLinkReset(
        show,
        useCallback(() => {
            setApplicationSearch('');
            setApplicationStatus('all');
            setApplicationDepartment('all');
        }, [
            setApplicationSearch,
            setApplicationStatus,
            setApplicationDepartment,
        ])
    );
    const assignmentControls = useTableControls({
        rows: assignments,
        columns: ASSIGNMENT_COLUMNS,
        statusOf: assignmentCourseOf,
        persist: useTableParams('leaders'),
    });
    const assignedCourseIds = useMemo(
        () => new Set(assignments.map((a) => a.course?._id).filter(Boolean)),
        [assignments]
    );
    const courseSiStatusOf = useCallback(
        (course) => (assignedCourseIds.has(course._id) ? 'assigned' : 'needs'),
        [assignedCourseIds]
    );
    const courseColumns = useMemo(
        () => [
            courseColumn(
                (course) => course,
                (course) => course.section
            ),
            {
                key: 'siStatus',
                label: 'SI Status',
                type: 'status',
                order: ['needs', 'assigned'],
                value: courseSiStatusOf,
                csv: (course) => COURSE_SI_STATUS[courseSiStatusOf(course)],
            },
        ],
        [courseSiStatusOf]
    );
    const courseControls = useTableControls({
        rows: courses,
        columns: courseColumns,
        statusOf: courseSiStatusOf,
        persist: useTableParams('courses'),
    });
    const studentApplicationControls = useTableControls({
        rows: studentApplications,
        columns: STUDENT_APPLICATION_COLUMNS,
        statusOf: studentApplicationStatusOf,
        persist: useTableParams('students'),
    });
    const assignmentCourseOptions = courseOptionsFor(
        assignments,
        (asg) => asg.course
    );

    const isViewOnly = VIEW_ONLY_STATUSES.includes(selectedApplication?.status);
    // Same statuses the server's requestRevision accepts
    const canReturnForRevision = ['submitted', 'under_review'].includes(
        selectedApplication?.status
    );

    const handleOpenDecision = (application) => {
        setSelectedApplication(application);
        setDecisionNotes('');
        setShowDecisionModal(true);
    };

    const handleDecision = async (decisionType) => {
        if (!selectedApplication) return;

        // Map button labels to API decision types
        const typeMap = {
            approve: 'approved',
            reject: 'denied',
            waitlist: 'waitlisted',
        };

        try {
            setSubmittingDecision(true);
            setError(null);

            await decisionService.createDecision({
                applicationId: selectedApplication._id,
                programType: 'si',
                type: typeMap[decisionType] || decisionType,
                term: selectedApplication.term || selectedTerm?.code,
                notes: decisionNotes || undefined,
            });

            setShowDecisionModal(false);
            setSelectedApplication(null);
            setDecisionNotes('');

            // Refresh applications list for the current term
            if (selectedTerm?.code) {
                await loadTermData(selectedTerm.code);
            }
        } catch (err) {
            const message =
                err.response?.data?.message ||
                'Failed to submit decision. Please try again.';
            setError(message);
        } finally {
            setSubmittingDecision(false);
        }
    };

    const handleReturnForRevision = async () => {
        if (!selectedApplication) return;

        if (!decisionNotes.trim()) {
            setError(
                'Please provide notes explaining what needs to be revised.'
            );
            return;
        }

        try {
            setSubmittingDecision(true);
            setError(null);

            await applicationService.requestRevision(
                selectedApplication._id,
                decisionNotes
            );

            setShowDecisionModal(false);
            setSelectedApplication(null);
            setDecisionNotes('');

            if (selectedTerm?.code) {
                await loadTermData(selectedTerm.code);
            }
        } catch (err) {
            const message =
                err.response?.data?.message ||
                'Failed to return application for revision. Please try again.';
            setError(message);
        } finally {
            setSubmittingDecision(false);
        }
    };

    // Where the requests stand is the Overview's Requests line, from one
    // source; the header keeps the figures nothing else on the tab shows
    const displayStats = [
        {
            value:
                stats?.activeLeaders ??
                assignments.filter((a) => a.status === 'active').length,
            label: 'Active SI Leaders',
            color: 'success',
        },
        {
            value: stats?.supportedCourses ?? courses.length,
            label: 'Supported Courses',
            color: 'primary',
        },
    ];

    const manageDates = () => {
        selectTab('settings', 'deadlines');
        setDeadlinesOpen(true);
    };

    const navGroups = [
        {
            key: 'overview',
            label: 'Overview',
            tabs: [{ key: 'overview', label: 'Overview' }],
        },
        {
            key: 'requests',
            label: 'Requests',
            tabs: [
                {
                    key: 'applications',
                    label: `Support requests (${cutOffTotal ?? applications.length})`,
                },
                { key: 'decisions', label: 'Decisions' },
            ],
        },
        {
            key: 'hiring',
            label: 'Hiring',
            tabs: [
                studentApplicationEnabled && {
                    key: 'student-applications',
                    label: `Student Applicants (${studentAppsFailed ? '-' : studentApplications.length})`,
                },
                {
                    key: 'assignments',
                    label: `SI Leaders (${assignments.length})`,
                },
            ].filter(Boolean),
        },
        {
            key: 'courses',
            label: 'Courses',
            tabs: [{ key: 'courses', label: `Courses (${courses.length})` }],
        },
        {
            key: 'settings',
            label: 'Settings',
            tabs: [{ key: 'settings', label: 'Settings' }],
        },
    ];

    const settingsSections = [
        {
            key: 'courses-served',
            title: 'Courses served',
            content: (
                <>
                    <ProgramCoursePrefixes programType="si" programLabel="SI" />
                </>
            ),
        },
        {
            key: 'deadlines',
            title: 'Application deadlines',
            content: (
                <ProgramDeadlinesSection
                    programType="si"
                    programLabel="SI"
                    programName="Supplemental Instruction"
                    selectedTerm={selectedTerm}
                    onTermUpdated={handleTermUpdated}
                    show={deadlinesOpen}
                    onShow={() => setDeadlinesOpen(true)}
                    onHide={() => setDeadlinesOpen(false)}
                />
            ),
        },
        {
            key: 'notifications',
            title: 'Notifications',
            content: <ProgramNotificationSettings programType="si" />,
        },
        {
            key: 'training-dates',
            title: 'Training dates',
            content: (
                <TrainingDatesCard
                    programType="si"
                    termCode={selectedTerm?.code}
                    termName={termDisplayName(selectedTerm)}
                />
            ),
        },
        {
            key: 'decision-emails',
            title: 'Decision emails',
            content: <DecisionEmailTemplates programType="si" />,
        },
        {
            key: 'program-page',
            title: 'Program page link',
            content: <ProgramPageLinkCard programType="si" programLabel="SI" />,
        },
    ];

    if (!selectedTerm && !loading) {
        return (
            <Container className="py-5">
                {error ? (
                    <Alert variant="danger">{error}</Alert>
                ) : (
                    <Alert variant="warning">
                        No terms configured. Ask an administrator to create a
                        term in the Admin panel.
                    </Alert>
                )}
            </Container>
        );
    }

    return (
        <Container className="py-4">
            <ProgramTermHeader
                programType="si"
                programLabel="SI"
                programName="Supplemental Instruction"
                programColor="var(--psh-si-color)"
                terms={terms}
                selectedTerm={selectedTerm}
                onTermChange={handleTermChange}
                stats={displayStats}
                statsLoading={loading}
                statsFailed={loadFailed}
                compact={activeTab !== 'overview'}
            />

            {error && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}
            {successMessage && (
                <Alert
                    variant="success"
                    dismissible
                    onClose={() => setSuccessMessage(null)}
                >
                    {successMessage}
                </Alert>
            )}

            {loadFailed ? (
                <LoadError
                    what="the SI program data"
                    onRetry={() => loadTermData(selectedTerm.code)}
                />
            ) : loading ? (
                <LoadingState rows={4} label="Loading SI program data" />
            ) : (
                <Tab.Container
                    activeKey={activeTab}
                    generateChildId={generateChildId}
                >
                    <DashboardNav
                        ariaLabel="Dashboard sections"
                        groups={navGroups}
                        activeTab={activeTab}
                        onSelect={selectTab}
                    />
                    {activeTab === 'overview' && (
                        <>
                            <MissingDatesAlert
                                programType="si"
                                programLabel="SI"
                                selectedTerm={selectedTerm}
                                onManageDates={manageDates}
                            />
                            {!loadFailed && (
                                <BudgetStatus
                                    budget={budget}
                                    failed={budgetFailed}
                                    loading={loading}
                                    onRetry={() =>
                                        loadTermData(selectedTerm.code)
                                    }
                                    termName={termDisplayName(selectedTerm)}
                                    unit="SI Leader"
                                    unitPlural="SI Leaders"
                                />
                            )}
                        </>
                    )}
                    <Tab.Content>
                        <Tab.Pane eventKey="overview" mountOnEnter>
                            <CoordinatorOverview
                                active={activeTab === 'overview'}
                                programType="si"
                                term={selectedTerm?.code}
                                termName={termDisplayName(selectedTerm)}
                                tabs={{
                                    requests: 'applications',
                                    positions: null,
                                }}
                            />
                        </Tab.Pane>

                        {/* Applications Tab */}
                        <Tab.Pane eventKey="applications">
                            {applicationList && (
                                <ListFilterNote
                                    show={show}
                                    list={applicationList}
                                    count={listedApplications.length}
                                    programLabel="SI"
                                    termName={termDisplayName(selectedTerm)}
                                    noun="requests"
                                    onClear={clearShow}
                                />
                            )}
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-white">
                                    <TableControlsBar
                                        controls={applicationControls}
                                        total={
                                            cutOffTotal ?? applications.length
                                        }
                                        noun="support requests"
                                        searchTitle="Search support requests"
                                        searchLabel="Search support requests by faculty name, submitter or course"
                                        searchPlaceholder="Name or course"
                                        statusLabel="Filter by request status"
                                        statusOptions={PROGRAM_OUTCOME_OPTIONS}
                                        departmentOptions={
                                            applicationDepartmentOptions
                                        }
                                        exportFilename={`si-applications-${selectedTerm?.code}.csv`}
                                    />
                                </Card.Header>
                                <Card.Body className="p-0">
                                    {cutOffTotal !== null && (
                                        <Alert
                                            variant="warning"
                                            className="m-3"
                                        >
                                            Showing the newest{' '}
                                            {applications.length} of{' '}
                                            {cutOffTotal} support requests for
                                            this term. Older ones aren&apos;t
                                            listed, so search and the filters
                                            don&apos;t cover them.
                                        </Alert>
                                    )}
                                    {asCards ? (
                                        <RequestCardList
                                            label="Support requests"
                                            items={applicationControls.rows.map(
                                                requestCardOf
                                            )}
                                        />
                                    ) : (
                                        <Table
                                            responsive
                                            hover
                                            className="mb-0"
                                        >
                                            <thead className="bg-light">
                                                <tr>
                                                    {APPLICATION_COLUMNS.map(
                                                        (column) => (
                                                            <SortableTh
                                                                key={column.key}
                                                                column={column}
                                                                controls={
                                                                    applicationControls
                                                                }
                                                            />
                                                        )
                                                    )}
                                                    <th
                                                        className={
                                                            SECONDARY_COLUMN
                                                        }
                                                    >
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {applicationControls.rows.map(
                                                    (app) => (
                                                        <tr key={app._id}>
                                                            <td>
                                                                <FacultyCell
                                                                    app={app}
                                                                    showEmail
                                                                />
                                                            </td>
                                                            <td>
                                                                <span className="fw-semibold me-1">
                                                                    {
                                                                        app
                                                                            .course
                                                                            ?.subject
                                                                    }{' '}
                                                                    {
                                                                        app
                                                                            .course
                                                                            ?.courseNumber
                                                                    }
                                                                </span>
                                                                <br />
                                                                <small>
                                                                    {
                                                                        app
                                                                            .course
                                                                            ?.title
                                                                    }
                                                                </small>
                                                                {modalityText(
                                                                    app
                                                                ) && (
                                                                    <small className="text-muted d-block d-lg-none">
                                                                        {modalityText(
                                                                            app
                                                                        )}
                                                                    </small>
                                                                )}
                                                            </td>
                                                            <td
                                                                className={
                                                                    SECONDARY_COLUMN_LG
                                                                }
                                                            >
                                                                {modalityText(
                                                                    app
                                                                ) || '-'}
                                                            </td>
                                                            <td
                                                                className={
                                                                    SECONDARY_COLUMN
                                                                }
                                                            >
                                                                #
                                                                {getSIRank(app)}
                                                            </td>
                                                            <td
                                                                className={
                                                                    SECONDARY_COLUMN
                                                                }
                                                            >
                                                                {app.submittedAt
                                                                    ? formatAnswerDay(
                                                                          app.submittedAt
                                                                      )
                                                                    : '-'}
                                                            </td>
                                                            <td>
                                                                {appStatusPill(
                                                                    app
                                                                )}
                                                            </td>
                                                            <td
                                                                className={
                                                                    SECONDARY_COLUMN
                                                                }
                                                            >
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    onClick={() =>
                                                                        handleOpenDecision(
                                                                            app
                                                                        )
                                                                    }
                                                                >
                                                                    Review
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    )
                                                )}
                                            </tbody>
                                        </Table>
                                    )}
                                    <TableEmpty
                                        controls={applicationControls}
                                        noun="support requests"
                                        emptyText={`No support requests for ${termDisplayName(selectedTerm) || 'this term'}.`}
                                    />
                                </Card.Body>
                            </Card>
                            <UnsubmittedDrafts
                                programType="si"
                                term={selectedTerm?.code}
                                forceOpen={
                                    activeTab === 'applications' &&
                                    show === DRAFTS_SHOW
                                }
                            />
                        </Tab.Pane>

                        {/* Assignments Tab */}
                        <Tab.Pane eventKey="assignments">
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-white">
                                    <TableControlsBar
                                        controls={assignmentControls}
                                        noun="SI leaders"
                                        searchLabel="Search SI leaders by name, course or schedule"
                                        searchPlaceholder="Search by SI leader or course..."
                                        statusLabel="Filter SI leaders by course"
                                        allLabel="All courses"
                                        statusOptions={assignmentCourseOptions}
                                        exportFilename={`si-leaders-${selectedTerm?.code}.csv`}
                                    />
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <Table responsive hover className="mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                {ASSIGNMENT_COLUMNS.map(
                                                    (column) => (
                                                        <SortableTh
                                                            key={column.key}
                                                            column={column}
                                                            controls={
                                                                assignmentControls
                                                            }
                                                        />
                                                    )
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {assignmentControls.rows.map(
                                                (asg) => (
                                                    <tr key={asg._id}>
                                                        <td>
                                                            <strong>
                                                                {asg
                                                                    .peerSupporter
                                                                    ?.name ||
                                                                    'Unknown'}
                                                            </strong>
                                                            <small className="text-muted d-none d-md-block">
                                                                {
                                                                    asg
                                                                        .peerSupporter
                                                                        ?.email
                                                                }
                                                            </small>
                                                        </td>
                                                        <td>
                                                            <Badge
                                                                bg="secondary"
                                                                className="me-1"
                                                            >
                                                                {
                                                                    asg.course
                                                                        ?.subject
                                                                }{' '}
                                                                {
                                                                    asg.course
                                                                        ?.courseNumber
                                                                }
                                                            </Badge>
                                                            <br />
                                                            <small>
                                                                {
                                                                    asg.course
                                                                        ?.title
                                                                }
                                                            </small>
                                                        </td>
                                                        <td
                                                            className={
                                                                SECONDARY_COLUMN
                                                            }
                                                        >
                                                            {asg.hoursPerWeek ||
                                                                '-'}
                                                        </td>
                                                        <td
                                                            className={
                                                                SECONDARY_COLUMN
                                                            }
                                                        >
                                                            {asg.schedule?.sessions?.map(
                                                                (s, i) => (
                                                                    <small
                                                                        key={i}
                                                                        className="d-block"
                                                                    >
                                                                        {s.day}{' '}
                                                                        {s.time}
                                                                    </small>
                                                                )
                                                            ) || '-'}
                                                        </td>
                                                        <td>
                                                            <Badge
                                                                bg={
                                                                    asg.status ===
                                                                    'active'
                                                                        ? 'success'
                                                                        : 'secondary'
                                                                }
                                                                text={badgeTextFor(
                                                                    asg.status ===
                                                                        'active'
                                                                        ? 'success'
                                                                        : 'secondary'
                                                                )}
                                                            >
                                                                {asg.status ===
                                                                'active'
                                                                    ? 'Active'
                                                                    : asg.status}
                                                            </Badge>
                                                        </td>
                                                    </tr>
                                                )
                                            )}
                                        </tbody>
                                    </Table>
                                    <TableEmpty
                                        controls={assignmentControls}
                                        noun="SI leaders"
                                        emptyText={`No SI assignments for ${termDisplayName(selectedTerm) || 'this term'}.`}
                                    />
                                </Card.Body>
                            </Card>
                        </Tab.Pane>

                        {/* Courses Tab */}
                        <Tab.Pane eventKey="courses">
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-white">
                                    <TableControlsBar
                                        controls={courseControls}
                                        noun="courses"
                                        searchLabel="Search courses by code, section or title"
                                        searchPlaceholder="Search by course or section..."
                                        statusLabel="Filter courses by SI status"
                                        allLabel="All courses"
                                        statusOptions={COURSE_STATUS_OPTIONS}
                                        exportFilename={`si-courses-${selectedTerm?.code}.csv`}
                                    />
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <Table responsive hover className="mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                {courseColumns.map((column) => (
                                                    <SortableTh
                                                        key={column.key}
                                                        column={column}
                                                        controls={
                                                            courseControls
                                                        }
                                                    />
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {courseControls.rows.map(
                                                (course) => {
                                                    const hasAssignment =
                                                        assignedCourseIds.has(
                                                            course._id
                                                        );
                                                    return (
                                                        <tr key={course._id}>
                                                            <td>
                                                                <Badge
                                                                    bg="secondary"
                                                                    className="me-1"
                                                                >
                                                                    {
                                                                        course.subject
                                                                    }{' '}
                                                                    {
                                                                        course.courseNumber
                                                                    }
                                                                </Badge>
                                                                {course.section && (
                                                                    <small className="text-muted">
                                                                        {' '}
                                                                        Section{' '}
                                                                        {
                                                                            course.section
                                                                        }
                                                                    </small>
                                                                )}
                                                                <br />
                                                                <small>
                                                                    {
                                                                        course.title
                                                                    }
                                                                </small>
                                                            </td>
                                                            <td>
                                                                {hasAssignment ? (
                                                                    <Badge bg="success">
                                                                        SI
                                                                        Assigned
                                                                    </Badge>
                                                                ) : (
                                                                    <Badge
                                                                        bg="warning"
                                                                        text="dark"
                                                                    >
                                                                        Needs SI
                                                                    </Badge>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                }
                                            )}
                                        </tbody>
                                    </Table>
                                    <TableEmpty
                                        controls={courseControls}
                                        noun="courses"
                                        emptyText={`No SI-eligible courses for ${termDisplayName(selectedTerm) || 'this term'}.`}
                                    />
                                </Card.Body>
                            </Card>
                        </Tab.Pane>

                        {/* Student Applicants Tab. Gated with the same flag as the
                        routes it navigates into: with the flag off the list is
                        empty and Review leads nowhere, so the tab would read
                        "Student Applicants (0)" and advertise a feature that
                        is not there. */}
                        {studentApplicationEnabled && (
                            <Tab.Pane eventKey="student-applications">
                                {studentAppsFailed ? (
                                    <LoadError
                                        what="the student applicants"
                                        onRetry={() =>
                                            loadTermData(selectedTerm.code)
                                        }
                                    />
                                ) : (
                                    <Card className="border-0 shadow-sm">
                                        <Card.Header className="bg-white">
                                            <TableControlsBar
                                                controls={
                                                    studentApplicationControls
                                                }
                                                noun="applicants"
                                                searchLabel="Search applicants by name, email or course"
                                                searchPlaceholder="Search by name, email, or course..."
                                                statusLabel="Filter by student application status"
                                                statusOptions={
                                                    STUDENT_APPLICATION_STATUS_OPTIONS
                                                }
                                                exportFilename={`si-student-applicants-${selectedTerm?.code}.csv`}
                                            />
                                        </Card.Header>
                                        <Card.Body className="p-0">
                                            <Table
                                                responsive
                                                hover
                                                className="mb-0"
                                            >
                                                <thead className="bg-light">
                                                    <tr>
                                                        {STUDENT_APPLICATION_COLUMNS.map(
                                                            (column) => (
                                                                <SortableTh
                                                                    key={
                                                                        column.key
                                                                    }
                                                                    column={
                                                                        column
                                                                    }
                                                                    controls={
                                                                        studentApplicationControls
                                                                    }
                                                                />
                                                            )
                                                        )}
                                                        <th
                                                            className={
                                                                SECONDARY_COLUMN
                                                            }
                                                        >
                                                            Actions
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {studentApplicationControls.rows.map(
                                                        (app) => (
                                                            <tr key={app._id}>
                                                                <td>
                                                                    <strong>
                                                                        {app
                                                                            .student
                                                                            ?.name ||
                                                                            'Unknown'}
                                                                    </strong>
                                                                    <Button
                                                                        variant="outline-primary"
                                                                        size="sm"
                                                                        className="d-block d-md-none mt-2"
                                                                        onClick={() =>
                                                                            navigate(
                                                                                `/student-applications/${app._id}`
                                                                            )
                                                                        }
                                                                    >
                                                                        Review
                                                                    </Button>
                                                                </td>
                                                                <td
                                                                    className={
                                                                        SECONDARY_COLUMN
                                                                    }
                                                                >
                                                                    <small className="text-muted">
                                                                        {app
                                                                            .student
                                                                            ?.email ||
                                                                            '-'}
                                                                    </small>
                                                                </td>
                                                                <td>
                                                                    {app
                                                                        .coursePreferences
                                                                        ?.length >
                                                                    0
                                                                        ? app.coursePreferences.map(
                                                                              (
                                                                                  pref,
                                                                                  idx
                                                                              ) => (
                                                                                  <Badge
                                                                                      key={
                                                                                          idx
                                                                                      }
                                                                                      bg="secondary"
                                                                                      className="me-1 mb-1"
                                                                                  >
                                                                                      {pref
                                                                                          .course
                                                                                          ?.subject ||
                                                                                          ''}{' '}
                                                                                      {pref
                                                                                          .course
                                                                                          ?.courseNumber ||
                                                                                          ''}
                                                                                  </Badge>
                                                                              )
                                                                          )
                                                                        : positionChoiceSummary(
                                                                              app
                                                                          ) ||
                                                                          '-'}
                                                                </td>
                                                                <td
                                                                    className={
                                                                        SECONDARY_COLUMN
                                                                    }
                                                                >
                                                                    {app.submittedAt
                                                                        ? formatAnswerDay(
                                                                              app.submittedAt
                                                                          )
                                                                        : '-'}
                                                                </td>
                                                                <td>
                                                                    <StatusPill
                                                                        family="studentApplication"
                                                                        value={
                                                                            app.status
                                                                        }
                                                                    />
                                                                </td>
                                                                <td
                                                                    className={
                                                                        SECONDARY_COLUMN
                                                                    }
                                                                >
                                                                    <Button
                                                                        variant="outline-primary"
                                                                        size="sm"
                                                                        onClick={() =>
                                                                            navigate(
                                                                                `/student-applications/${app._id}`
                                                                            )
                                                                        }
                                                                    >
                                                                        Review
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        )
                                                    )}
                                                </tbody>
                                            </Table>
                                            <TableEmpty
                                                controls={
                                                    studentApplicationControls
                                                }
                                                noun="applicants"
                                                emptyText={`No student applications found for ${termDisplayName(selectedTerm) || 'this term'}.`}
                                            />
                                        </Card.Body>
                                    </Card>
                                )}
                            </Tab.Pane>
                        )}

                        <Tab.Pane eventKey="decisions" mountOnEnter>
                            <ProgramDecisionsList
                                programType="si"
                                term={selectedTerm?.code}
                                termName={termDisplayName(selectedTerm)}
                            />
                        </Tab.Pane>

                        <Tab.Pane eventKey="settings" mountOnEnter>
                            <CoordinatorSettings
                                focusKey={
                                    activeTab === 'settings' ? show : null
                                }
                                sections={settingsSections}
                                onSelect={(key) => selectTab('settings', key)}
                            />
                        </Tab.Pane>
                    </Tab.Content>
                </Tab.Container>
            )}

            {/* Decision Modal */}
            <Modal
                aria-labelledby="sicd-modal-title-1"
                show={showDecisionModal}
                onHide={() => setShowDecisionModal(false)}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="sicd-modal-title-1">
                        Review Application
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedApplication && (
                        <>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <h6>Applicant Information</h6>
                                    <Table size="sm" borderless>
                                        <tbody>
                                            <tr>
                                                <td className="text-muted">
                                                    Name:
                                                </td>
                                                <td>
                                                    <strong>
                                                        {selectedApplication
                                                            .applicant?.name ||
                                                            'Unknown'}
                                                    </strong>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="text-muted">
                                                    Email:
                                                </td>
                                                <td>
                                                    {selectedApplication
                                                        .applicant?.email ||
                                                        '-'}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="text-muted">
                                                    SI Preference:
                                                </td>
                                                <td>
                                                    #
                                                    {getSIRank(
                                                        selectedApplication
                                                    )}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </Col>
                                <Col md={6}>
                                    <h6>Course Information</h6>
                                    <Table size="sm" borderless>
                                        <tbody>
                                            <tr>
                                                <td className="text-muted">
                                                    Course:
                                                </td>
                                                <td>
                                                    <strong>
                                                        {
                                                            selectedApplication
                                                                .course?.subject
                                                        }{' '}
                                                        {
                                                            selectedApplication
                                                                .course
                                                                ?.courseNumber
                                                        }
                                                    </strong>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="text-muted">
                                                    Title:
                                                </td>
                                                <td>
                                                    {selectedApplication.course
                                                        ?.title || '-'}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="text-muted">
                                                    Instructor:
                                                </td>
                                                <td>
                                                    {requestInstructorName(
                                                        selectedApplication
                                                    ) || 'TBD'}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </Col>
                            </Row>
                            {selectedApplication.qualifications?.experience && (
                                <div className="mb-3">
                                    <h6>Experience</h6>
                                    <p className="small bg-light p-3 rounded">
                                        {
                                            selectedApplication.qualifications
                                                .experience
                                        }
                                    </p>
                                </div>
                            )}
                            {selectedApplication.qualifications?.motivation && (
                                <div className="mb-3">
                                    <h6>Motivation</h6>
                                    <p className="small bg-light p-3 rounded">
                                        {
                                            selectedApplication.qualifications
                                                .motivation
                                        }
                                    </p>
                                </div>
                            )}
                            {selectedApplication.courseContext?.additionalNotes?.trim() && (
                                <div className="mb-3">
                                    <h6>Anything else we should know?</h6>
                                    <p className="small bg-light p-3 rounded">
                                        {
                                            selectedApplication.courseContext
                                                .additionalNotes
                                        }
                                    </p>
                                </div>
                            )}

                            {isViewOnly ? (
                                <Alert variant="secondary" className="mb-0">
                                    {selectedApplication.status === 'withdrawn'
                                        ? 'This application was withdrawn, so no decision can be made on it.'
                                        : isBeingRevisedByFaculty(
                                                selectedApplication
                                            )
                                          ? 'The faculty member is revising this application. You can decide on it once they resubmit.'
                                          : 'This application is back with the faculty member for revision. You can decide on it once they resubmit.'}
                                </Alert>
                            ) : (
                                <div className="mb-3">
                                    <h6>Decision Notes</h6>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        placeholder="Add optional notes about this decision..."
                                        value={decisionNotes}
                                        onChange={(e) =>
                                            setDecisionNotes(e.target.value)
                                        }
                                        disabled={submittingDecision}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                {isViewOnly ? (
                    <Modal.Footer>
                        <Button
                            variant="outline-primary"
                            onClick={() => setShowDecisionModal(false)}
                        >
                            Close
                        </Button>
                    </Modal.Footer>
                ) : (
                    <Modal.Footer className="d-flex justify-content-between">
                        <div>
                            {canReturnForRevision && (
                                <Button
                                    variant="outline-warning"
                                    onClick={handleReturnForRevision}
                                    disabled={submittingDecision}
                                >
                                    {submittingDecision
                                        ? 'Submitting...'
                                        : 'Return for Revision'}
                                </Button>
                            )}
                        </div>
                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-primary"
                                onClick={() => setShowDecisionModal(false)}
                                disabled={submittingDecision}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="outline-primary"
                                onClick={() => handleDecision('waitlist')}
                                disabled={submittingDecision}
                            >
                                {submittingDecision
                                    ? 'Submitting...'
                                    : 'Waitlist'}
                            </Button>
                            <Button
                                variant="danger"
                                onClick={() => handleDecision('reject')}
                                disabled={submittingDecision}
                            >
                                {submittingDecision
                                    ? 'Submitting...'
                                    : 'Decline'}
                            </Button>
                            <Button
                                variant="success"
                                onClick={() => handleDecision('approve')}
                                disabled={submittingDecision}
                            >
                                {submittingDecision
                                    ? 'Submitting...'
                                    : 'Approve'}
                            </Button>
                        </div>
                    </Modal.Footer>
                )}
            </Modal>
        </Container>
    );
}

export default SICoordinatorDashboard;
